// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import { Cart } from "@insite/client-framework/Services/CartService";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import translate from "@insite/client-framework/Translate";
import { PromotionModel } from "@insite/client-framework/Types/ApiModels";
import LocalizedCurrency from "@insite/content-library/Components/LocalizedCurrency";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { parseBoolean, StyledBox, StyledHr } from "@steiner/XDL/Utilities";
import React, { Fragment, PropsWithChildren } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    isLoading?: boolean;
    /**
     * If true, displays an empty cart total UI.
     * If false, displays amounts from the cart.
     * Default value: false
     */
    isCartEmpty?: boolean;
    /**
     * If true, displays tax, shipping and handling, and total amounts.
     * If false, does not display tax, shipping and handling, or total amounts.
     * Default value: true
     */
    showTaxAndShipping?: boolean;
    cart?: Cart;
    orderPromotions?: PromotionModel[];
    shippingPromotions?: PromotionModel[];
    discountTotal?: number;
    extendedStyles?: CartTotalDisplayStyles;
    /**
     * XNG-Changes
     * override CartTotal title
     * default value: Order Summary
     */
    overrideTitle?: string;
    /**
     * XNG-Changes
     * * Allow developers to inject additional content into the component.
     */
    children?: React.ReactNode;
    /**
     * XNG-Changes
     * hides the shipping and tax values if true
     */
    isCheckoutShippingPage?: boolean;
}

const mapStateToProps = (state: ApplicationState) => ({
    enableVat: getSettingsCollection(state).productSettings.enableVat,
    showPricing: state.context.showPricing,
    isCheckoutReviewAndSubmitPage: getCurrentPage(state).type === "CheckoutReviewAndSubmitPage",
});

type Props = OwnProps & ReturnType<typeof mapStateToProps>;

export interface CartTotalDisplayStyles {
    container?: GridContainerProps;
    labelGridItem?: GridItemProps;
    valueGridItem?: GridItemProps;
    subtotalLabel?: TypographyProps;
    subtotalValue?: TypographyProps;
    promotionLabel?: TypographyProps;
    promotionValue?: TypographyProps;
    discountsLabel?: TypographyProps;
    discountsValue?: TypographyProps;
    shippingAndHandlingLabel?: TypographyProps;
    shippingAndHandlingValue?: TypographyProps;
    otherChargesLabel?: TypographyProps;
    otherChargesValue?: TypographyProps;
    taxLabel?: TypographyProps;
    taxValue?: TypographyProps;
    totalLabel?: TypographyProps;
    totalValue?: TypographyProps;
    loadingSpinner?: LoadingSpinnerProps;
    // xng-change
    styledBoxContainer?: InjectableCss;
    styledHrGridItem?: GridItemProps;
    styledHr?: InjectableCss;
    fullWidthGridItem?: GridItemProps;
    orderSummaryGridItem?: GridItemProps;
    orderSummaryLabel?: TypographyPresentationProps;
}

export const cartTotalDisplayStyles: CartTotalDisplayStyles = {
    container: {
        gap: 10,
        css: css`
            background-color: ${getColor("common.accent")};
            padding: 20px;
        `,
    },
    labelGridItem: { width: 6 },
    valueGridItem: { width: 6 },
    subtotalValue: {
        css: css`
            margin-left: auto;
        `,
    },
    promotionValue: {
        css: css`
            margin-left: auto;
        `,
    },
    discountsValue: {
        css: css`
            margin-left: auto;
        `,
    },
    shippingAndHandlingValue: {
        css: css`
            margin-left: auto;
        `,
    },
    otherChargesValue: {
        css: css`
            margin-left: auto;
        `,
    },
    taxValue: {
        css: css`
            margin-left: auto;
        `,
    },
    totalLabel: { weight: "bold" },
    totalValue: {
        weight: "bold",
        css: css`
            margin-left: auto;
        `,
    },
    loadingSpinner: {
        size: 15,
        css: css`
            margin-top: auto;
            margin-bottom: auto;
            margin-left: auto;
        `,
    },
};

const OrderSummaryHeading = ({
    overrideTitle,
    styles,
}: {
    overrideTitle?: OwnProps["overrideTitle"];
    styles: CartTotalDisplayStyles;
}) => {
    return (
        <>
            <GridItem {...styles.orderSummaryGridItem}>
                <Typography {...styles.orderSummaryLabel}>{overrideTitle || translate("Order Summary")}</Typography>
            </GridItem>
            <GridItem {...styles.styledHrGridItem}>
                <StyledHr {...styles.styledHr} />
            </GridItem>
        </>
    );
};

const CartTotalDisplay = ({
    isCartEmpty = false,
    showTaxAndShipping = true,
    cart,
    orderPromotions,
    shippingPromotions,
    discountTotal,
    isLoading,
    enableVat,
    showPricing,
    isCheckoutReviewAndSubmitPage,
    extendedStyles,
    // XNG-Changes
    overrideTitle,
    children,
    isCheckoutShippingPage,
}: PropsWithChildren<Props>) => {
    const [styles] = React.useState(() => mergeToNew(cartTotalDisplayStyles, extendedStyles));
    if (!showPricing && !isCheckoutReviewAndSubmitPage) {
        return null;
    }

    if (isLoading || !cart) {
        return (
            <StyledBox {...styles.styledBoxContainer}>
                <GridContainer {...styles.container}>
                    <OrderSummaryHeading overrideTitle={overrideTitle} styles={styles} />
                    {showTaxAndShipping && (
                        <>
                            <GridItem {...styles.labelGridItem}>
                                <Typography {...styles.totalLabel}>{translate("Total")}</Typography>
                            </GridItem>
                            <GridItem {...styles.valueGridItem}>
                                <LoadingSpinner {...styles.loadingSpinner} />
                            </GridItem>
                        </>
                    )}
                    {!showTaxAndShipping && (
                        <>
                            <GridItem {...styles.labelGridItem}>
                                <Typography {...styles.subtotalLabel}>
                                    {translate("Subtotal") + (enableVat ? ` (${translate("Ex. VAT")})` : "")}
                                </Typography>
                            </GridItem>
                            <GridItem {...styles.valueGridItem}>
                                <LoadingSpinner {...styles.loadingSpinner} />
                            </GridItem>
                        </>
                    )}
                    {children}
                </GridContainer>
            </StyledBox>
        );
    }

    if (isCartEmpty) {
        return (
            <StyledBox {...styles.styledBoxContainer}>
                <GridContainer {...styles.container}>
                    <OrderSummaryHeading overrideTitle={overrideTitle} styles={styles} />
                    {showTaxAndShipping && (
                        <>
                            <GridItem {...styles.labelGridItem}>
                                <Typography {...styles.totalLabel}>{translate("Total")}</Typography>
                            </GridItem>
                            <GridItem {...styles.valueGridItem}>
                                <Typography {...styles.totalValue}>{`${cart.currencySymbol}0.00`}</Typography>
                            </GridItem>
                        </>
                    )}
                    {!showTaxAndShipping && (
                        <>
                            <GridItem {...styles.labelGridItem}>
                                <Typography {...styles.subtotalLabel}>
                                    {translate("Subtotal") + (enableVat ? ` (${translate("Ex. VAT")})` : "")}
                                </Typography>
                            </GridItem>
                            <GridItem {...styles.valueGridItem}>
                                <Typography {...styles.subtotalValue}>{`${cart.currencySymbol}0.00`}</Typography>
                            </GridItem>
                        </>
                    )}
                    {children}
                </GridContainer>
            </StyledBox>
        );
    }

    const filteredOrderPromotions = orderPromotions?.filter(o => o.amount > 0);
    const filteredShippingPromotions = shippingPromotions?.filter(o => o.amount > 0);

    return (
        <StyledBox {...styles.styledBoxContainer}>
            <GridContainer {...styles.container} data-test-selector={`cartTotal_${cart.id}`}>
                <OrderSummaryHeading overrideTitle={overrideTitle} styles={styles} />
                <GridItem {...styles.labelGridItem}>
                    <Typography {...styles.subtotalLabel}>
                        {translate("Subtotal") + (enableVat ? ` (${translate("Ex. VAT")})` : "")}
                    </Typography>
                </GridItem>
                <GridItem {...styles.valueGridItem}>
                    <Typography {...styles.subtotalValue} data-test-selector="cartTotal_subTotal">
                        {cart.orderSubTotalDisplay}
                    </Typography>
                </GridItem>
                {filteredOrderPromotions &&
                    filteredOrderPromotions.map(promotion => (
                        <Fragment key={promotion.id}>
                            <GridItem {...styles.labelGridItem}>
                                <Typography
                                    {...styles.promotionLabel}
                                    data-test-selector="cartTotal_orderPromotionName"
                                >
                                    {`${translate("Promotion")}: ${promotion.name}`}
                                </Typography>
                            </GridItem>
                            <GridItem {...styles.valueGridItem}>
                                <Typography
                                    {...styles.promotionValue}
                                    data-test-selector="cartTotal_orderPromotionValue"
                                >
                                    {`-${promotion.amountDisplay}`}
                                </Typography>
                            </GridItem>
                        </Fragment>
                    ))}
                {showTaxAndShipping && cart.shippingAndHandlingDisplay.length > 0 && (
                    <>
                        <GridItem {...styles.labelGridItem}>
                            <Typography {...styles.shippingAndHandlingLabel}>
                                {translate("Shipping & Handling")}
                            </Typography>
                        </GridItem>
                        <GridItem {...styles.valueGridItem}>
                            <Typography
                                {...styles.shippingAndHandlingValue}
                                data-test-selector="cartTotal_shippingAndHandlingAmount"
                            >
                                {isCheckoutShippingPage ? translate("-") : cart.shippingAndHandlingDisplay}
                            </Typography>
                        </GridItem>
                    </>
                )}
                {filteredShippingPromotions &&
                    filteredShippingPromotions.map(promotion => (
                        <Fragment key={promotion.id}>
                            <GridItem {...styles.labelGridItem}>
                                <Typography {...styles.promotionLabel}>
                                    {`${translate("Promotion")}: ${promotion.name}`}
                                </Typography>
                            </GridItem>
                            <GridItem {...styles.valueGridItem}>
                                <Typography
                                    {...styles.promotionValue}
                                    data-test-selector="cartTotal_shippingPromotionValue"
                                >
                                    {`-${promotion.amountDisplay}`}
                                </Typography>
                            </GridItem>
                        </Fragment>
                    ))}
                {showTaxAndShipping && cart.totalTaxDisplay.length > 0 && cart.customerOrderTaxes!.length === 0 && (
                    <>
                        <GridItem {...styles.labelGridItem}>
                            <Typography {...styles.taxLabel}>
                                {enableVat ? translate("VAT") : translate("Tax")}
                            </Typography>
                        </GridItem>
                        <GridItem {...styles.valueGridItem}>
                            <Typography {...styles.taxValue} data-test-selector="cartTotal_totalTaxDisplay">
                                {isCheckoutShippingPage ? translate("-") : cart.totalTaxDisplay}
                            </Typography>
                        </GridItem>
                    </>
                )}
                {/* xng-change */}
                {parseBoolean(cart.properties.failedToGetTaxFromErp) && (
                    <>
                        <GridItem {...styles.labelGridItem} />
                        <GridItem {...styles.valueGridItem}>
                            <Typography {...styles.taxValue} data-test-selector="cartTotal_totalTaxDisplay">
                                {siteMessage("STR_Estimate_Tax_Not_Included")}
                            </Typography>
                        </GridItem>
                    </>
                )}
                {showTaxAndShipping &&
                    [...cart.customerOrderTaxes!]
                        .sort((a, b) => a.sortOrder - b.sortOrder)
                        .map(tax => {
                            const key = `${tax.taxCode}_${tax.sortOrder}`;
                            return (
                                <Fragment key={key}>
                                    <GridItem {...styles.labelGridItem}>
                                        <Typography>{tax.taxDescription || translate("Tax")}</Typography>
                                    </GridItem>
                                    <GridItem {...styles.valueGridItem}>
                                        <Typography {...styles.taxValue}>
                                            {isCheckoutShippingPage ? translate("-") : tax.taxAmountDisplay}
                                        </Typography>
                                    </GridItem>
                                </Fragment>
                            );
                        })}
                {showTaxAndShipping && (
                    <>
                        <GridItem {...styles.styledHrGridItem}>
                            <StyledHr {...styles.styledHr} />
                        </GridItem>
                        <GridItem {...styles.labelGridItem}>
                            <Typography {...styles.totalLabel}>{translate("Total")}</Typography>
                        </GridItem>
                        <GridItem {...styles.valueGridItem}>
                            <Typography {...styles.totalValue} data-test-selector="cartTotal_orderGrandTotalDisplay">
                                {cart.orderGrandTotalDisplay}
                            </Typography>
                        </GridItem>
                    </>
                )}
                {discountTotal !== undefined && discountTotal > 0 && (
                    <>
                        <GridItem {...styles.labelGridItem}>
                            <Typography {...styles.discountsLabel}>{translate("Discounts")}</Typography>
                        </GridItem>
                        <GridItem {...styles.valueGridItem}>
                            <Typography {...styles.discountsValue} data-test-selector="cartTotal_totalDiscounts">
                                {`${translate("You saved")} `}{" "}
                                <LocalizedCurrency amount={discountTotal} currencySymbol={cart.currencySymbol} />
                            </Typography>
                        </GridItem>
                    </>
                )}
                {children}
            </GridContainer>
        </StyledBox>
    );
};

export default connect(mapStateToProps)(CartTotalDisplay);
